<template>
    <div v-if="$store.state.showOverlay" id="overlay">
        <div id="loading">
            <div id="loading-gif"></div>
            <p> {{ showMessage }} </p>
            <div v-if="showProgress" class="section">
                <div>{{ progressText }}</div>
                <div class="progress-bar">
                    <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
		        </div>
            </div> 
            <button v-if="isCancelable" @click="cancelOcr" class="cancel-button">{{ $t("LoadingMessages.cancel") }}</button>
        </div>
    </div>
</template>

<script>
import EnvProvider from "jvjr-docker-env";

export default {
    name: "LoadingOverlay",
    props: {
        ocrId: String,
        message: String,
    },
    data() {
        return {
            showMessage: "",
            showOverlay: true,
            showProgress: false,
            progressText: "",
            progressPercent: 0,
            pollingInterval: null,
            pollingErrorCount: 0,
            maxPollingErrors: 30,
            processingStarted: false,
            canceled: false,
        };
    },
    watch: {
        ocrId(newVal) {
            if (newVal) {
                this.startPolling();
            } else {
                this.clearPolling();
                this.resetProgressUI();
                this.canceled = false;
                this.processingStarted = false;
            }
        },
    },
    mounted() {
        this.showMessage = JSON.parse(JSON.stringify(this.message))
        if (this.ocrId) {
            this.startPolling();
        }
    },
    beforeUnmount() {
        this.clearPolling();
    },
    computed: {
        isCancelable() {
        return (
            !!this.ocrId &&
            this.processingStarted &&
            !this.canceled &&
            this.showMessage !== this.$t("LoadingMessages.processingResults")
        );
    }
    },
    methods: {
        async cancelOcr() {
            try {
                const response = await this.axios.post(`${EnvProvider.value("API_URL")}${this.$root.API_OCR_CANCEL}/${this.ocrId}`);
                this.canceled = true;
            } catch (error) {
                console.error('Could not cancel OCR')
                this.canceled = false;
            };
        },
        startPolling() {
            if (!this.ocrId) return;
            this.showMessage = this.$t("LoadingMessages.ocrStart");
            this.showProgress = true;
            this.pollingErrorCount = 0;
            this.processingStarted = false;
            this.pollProgress();
            this.pollingInterval = setInterval(this.pollProgress, 1000);
        },
        async pollProgress() {
            try {
                const response = await this.axios.get(`${EnvProvider.value("API_URL")}${this.$root.API_OCR_PROGRESS}/${this.ocrId}`);
                const progressData = response.data;

                // Reset error count on success
                this.pollingErrorCount = 0;

                if (progressData.status == "processing") {
                    this.showMessage = this.$t("LoadingMessages.ocrProcessing");
                    this.processingStarted = true;
                    this.updateProgressUI(progressData.progress);

                    if (progressData?.finished === true) {
                        this.clearPolling();
                    }
                } else if (progressData.status == "queued") {
                    this.showMessage = this.$t("LoadingMessages.ocrQueued");
                    this.updateQueue(progressData);
                }
            } catch (error) {
                this.pollingErrorCount++;
                console.error(`OCR progress polling failed (${this.pollingErrorCount}/${this.maxPollingErrors}):`, error);

                // If we previously saw a processing status and now we get 400, treat it as finished
                if (this.processingStarted && error.response?.status === 400) {
                    console.warn("Received 400 after processing started. Assuming OCR is finished.");
                    this.clearPolling();
                    this.resetProgressUI();
                    if(this.canceled){
                        this.showMessage = this.$t("LoadingMessages.cancelingOCR");
                    } else {
                        this.showMessage = this.$t("LoadingMessages.processingResults");
                    }
                    return;
                }

                if (this.pollingErrorCount >= this.maxPollingErrors) {
                    this.clearPolling();
                    console.error("Max polling retries reached. Stopping.");
                }
            }
        },

        getUnit(unit){
            var textLabel = "";
            switch (unit) {
                case "page":
                    textLabel = this.$t("LoadingMessages.page");
                    break;
                case "image":
                    textLabel = this.$t("LoadingMessages.image");
                    break;
                default:
                    textLabel = unit || "";
            }
            return textLabel;
        },
        getDescription(desc){
            var textLabel = "";
            switch (desc) {     
                case "Linearizing":
                    textLabel = this.$t("LoadingMessages.linearizing");
                    break;
                case "Recompressing JPEGs":
                    textLabel = this.$t("LoadingMessages.recompressingJpegs");
                    break;
                case "Deflating JPEGs":
                    textLabel = this.$t("LoadingMessages.deflatingJpegs");
                    break;
                case "PNGs":
                    textLabel = this.$t("LoadingMessages.pngs");
                    break;
                case "PDF/A conversion":
                    textLabel = this.$t("LoadingMessages.pdfaConversion");
                    break;
                case "Grafting hOCR to PDF":
                    textLabel = this.$t("LoadingMessages.graftingHocrToPdf");
                    break;
                case "Image processing":
                    textLabel = this.$t("LoadingMessages.imageProcessing");
                    break;
                case "Scanning contents":
                    textLabel = this.$t("LoadingMessages.scanningContents");
                    break;
                default:
                    textLabel = desc || "";
                }
            return textLabel;
        },
        updateProgressUI(progressData) {
            const { total, desc, unit, progress_bar } = progressData;
            let percentValue = 0;
            let textLabel = this.getDescription(desc) || "";

            if (total && total > 0) {
                percentValue = Math.round((progress_bar / total) * 100);
                textLabel += ` - ${progress_bar} / ${total} ${this.getUnit(unit)}`;
            } else if (unit === "%") {
                percentValue = Math.round(progress_bar);
                textLabel += ` - ${progress_bar}%`;
            }

            this.progressText = textLabel;
            this.progressPercent = percentValue;
        },
        updateQueue(queueData) {
            // Implement queue position update logic here
            let textLabel = `${this.$t("LoadingMessages.queuePosition")} ${queueData.queue_position}`;
            console.log(textLabel)
            this.progressText = textLabel;
        },
        resetProgressUI() {
            this.showProgress = false;
            this.progressText = "";
            this.progressPercent = 0;
        },
        clearPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped src="../assets/css/components/LoadingOverlay.scss"></style>